import { Button, Progress, Table, Tag, Typography, notification } from 'antd';
import Column from 'antd/lib/table/Column';
import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import settings from '../../settings';
import {
  PartnerOnboardingStatuses,
  deletePartnerOnboardingMutation,
  partnerTypeLabel,
} from './constants';
import ViewPartnerOnboardingModal from './ViewPartnerOnboardingModal';

const { Text } = Typography;

function ListPartnerOnboardings({
  loading,
  error,
  partnerOnboardings,
  next,
  hasNextPage,
  refetch,
}) {
  // Without this a failed query renders as an empty table, indistinguishable from
  // "no onboardings" -- the page already passes `error` down, it was just dropped.
  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error',
        description: error.message,
      });
    }
  }, [error]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });
  const handleTableChange = useCallback((params) => {
    setPagination(params);
  }, []);

  const [viewingId, setViewingId] = useState(null);
  const [deleteOnboarding] = useMutation(deletePartnerOnboardingMutation);
  const [deleting, setDeleting] = useState(null);

  const handleDelete = useCallback(
    async (_id) => {
      setDeleting(_id);
      try {
        await deleteOnboarding({ variables: { _id } });
        notification.success({
          message: 'Deleted',
          description: 'Record deleted successfully',
        });
        refetch();
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
      setDeleting(null);
    },
    [deleteOnboarding, refetch],
  );

  return (
    <>
      <Table
        dataSource={partnerOnboardings}
        loading={loading}
        rowKey="_id"
        pagination={{
          ...pagination,
          total: partnerOnboardings?.length,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        style={{ width: '100%' }}
        scroll={{ x: 900 }}
      >
        <Column
          title="Status"
          dataIndex="status"
          key="status"
          render={(val) => {
            const s = PartnerOnboardingStatuses[val];
            return s ? <Tag color={s.color}>{s.label}</Tag> : val;
          }}
        />
        <Column
          title="Region"
          dataIndex="region"
          key="region"
          render={(val, record) => record.regionName || val}
        />
        <Column
          title="Contact"
          dataIndex="contact"
          key="contact"
          render={(contact, record) => {
            if (!contact?.fullName) {
              return (
                <Text type="secondary" italic>
                  Unknown contact
                </Text>
              );
            }
            if (!record.canViewContact || !record.contactId) {
              return contact.fullName;
            }
            return (
              <Link to={`/contact/view/${record.contactId}/partner-onboarding`}>
                {contact.fullName}
              </Link>
            );
          }}
        />
        <Column
          title="Partner Type"
          dataIndex="partnerType"
          key="partnerType"
          render={(val) => partnerTypeLabel(val) || '—'}
        />
        <Column
          title="Coordinator"
          dataIndex="coordinatorContactId"
          key="coordinator"
          render={(_, record) => {
            const name = record.coordinatorContact?.fullName;
            if (!name) return <span style={{ color: '#bbb' }}>—</span>;
            // Same gate as the Contact column: contact read is region-scoped, so only
            // link where the caller could actually open the page.
            if (
              !record.canViewCoordinatorContact ||
              !record.coordinatorContactId
            ) {
              return name;
            }
            return (
              <Link
                to={`/contact/view/${record.coordinatorContactId}/partner-onboarding`}
              >
                {name}
              </Link>
            );
          }}
        />
        <Column
          title="Progress"
          key="progress"
          width={100}
          render={(_, record) => {
            const done = record.tasksDoneCount ?? 0;
            const total = record.tasksTotalCount ?? 0;
            if (total === 0) return <span style={{ color: '#bbb' }}>—</span>;
            return (
              <Progress
                percent={Math.round((done / total) * 100)}
                size="small"
                format={() => `${done}/${total}`}
                status={done === total ? 'success' : 'normal'}
              />
            );
          }}
        />
        <Column
          title="Updated"
          dataIndex="updatedAt"
          key="updatedAt"
          render={(val) => (val ? new Date(val).toLocaleDateString() : '')}
        />
        <Column
          title="Actions"
          key="actions"
          render={(_, record) => (
            <Button type="link" onClick={() => setViewingId(record._id)}>
              View
            </Button>
          )}
        />
      </Table>
      {hasNextPage && (
        <Button onClick={next} loading={loading}>
          Load more
        </Button>
      )}
      <ViewPartnerOnboardingModal
        _id={viewingId}
        visible={!!viewingId}
        onCancel={() => setViewingId(null)}
        onSuccess={() => {
          setViewingId(null);
          refetch();
        }}
        onDelete={handleDelete}
        deleting={deleting}
      />
    </>
  );
}

export default ListPartnerOnboardings;
